import { Link } from "react-router-dom";
import { HelpCircle, Swords, Users, Crown, Coins, PawPrint, Trophy, Shield } from "lucide-react";

export default function FAQ() {
  const faqs = [
    {
      icon: HelpCircle,
      question: "What is Lanista Manager?",
      answer: "Lanista Manager is a free-to-play online gladiator management game. You play as a Roman lanista (gladiator trainer) who recruits fighters, trains them in real-time, forges equipment, captures and breeds beasts, and battles other players in tactical PvP arena combat."
    },
    {
      icon: Coins,
      question: "Is Lanista really free to play?",
      answer: "Yes! Lanista is completely free to play in your browser with no download required. There's an optional VIP membership ($1.99 first month, then $4.49/month) that adds convenience features like extra roster slots and faster training, but all core gameplay is free forever."
    },
    {
      icon: Swords,
      question: "How do gladiator battles work?",
      answer: "Battles are tactical auto-simulations based on your gladiator's stats, skills, equipment, and chosen fighting style. You'll see live turn-by-turn commentary as the fight unfolds. You can challenge other players in ranked PvP, unranked scrimmages, or participate in tournaments and community events."
    },
    {
      icon: Shield,
      question: "What are the different gladiator classes?",
      answer: "There are six gladiator classes: Murmillo (sword & large shield), Retiarius (net & trident), Thraex (curved sword & small shield), Hoplomachus (spear & medium shield), Secutor (gladius & shield, built to counter Retiarius), and Dimachaerus (dual swords). Each class has unique equipment, fighting styles, and skill trees."
    },
    {
      icon: Users,
      question: "How does real-time training work?",
      answer: "Training happens automatically at scheduled times: 00:00, 08:00, and 16:00 (server time) every day. You set which stats your gladiators should train, and they'll gain improvements at each session — even while you're offline. This means you don't need to be online 24/7 to progress."
    },
    {
      icon: PawPrint,
      question: "What is the Beast System (Vivarium)?",
      answer: "The Vivarium lets you capture and breed exotic beasts from across the Roman Empire, including Atlas Bears, Barbary Lions, Anatolian Leopards, Eurasian Wolves, Nile Crocodiles, and Barbary Macaques. Beasts can be equipped to gladiators to provide combat bonuses. You can breed beasts to create stronger offspring with enhanced stats."
    },
    {
      icon: Crown,
      question: "What is the Golden Crown?",
      answer: "The Golden Crown is awarded to the #1 ranked gladiator. Crown holders must defend their title by accepting all challenges. If a crown holder loses a fight or drops below rank #8, the crown passes to the new #1 ranked gladiator. Crown holders earn special rewards for each successful defense."
    },
    {
      icon: Trophy,
      question: "How do I climb the rankings?",
      answer: "Win ranked PvP battles to increase your gladiator's rating. Higher ratings place you higher on the leaderboards. Build win streaks for bonus rating gains. Keep your gladiators trained, properly equipped, and use tactical skill tree unlocks to maximize your chances of victory."
    },
    {
      icon: HelpCircle,
      question: "Do gladiators age and retire?",
      answer: "Yes! Gladiators age in real-time. They enter their prime around age 24-28, then gradually decline. You can retire gladiators before old age forces them out, immortalizing them in the public Hall of the Retired. Managing the lifecycle of your roster is a key strategic element."
    },
    {
      icon: Coins,
      question: "How do I earn gold?",
      answer: "Gold is earned through winning battles (both PvP and PvE), completing daily login rewards, participating in tournaments and events, finishing timed quests, selling equipment and beasts at the auction house, and receiving referral bonuses when your invited friends play."
    },
    {
      icon: Users,
      question: "What are Alliances?",
      answer: "Alliances are player guilds where you can team up with other lanistas. Alliance members share a treasury, chat together, compete in alliance rankings, and participate in special co-op events like world boss battles."
    },
    {
      icon: Trophy,
      question: "What is the Imperial Campaign?",
      answer: "The Imperial Campaign is a PvE progression mode where you guide your gladiators through provinces across the Roman Empire (Italia, Thracia, Aegyptus, Britannia). Each province has branching paths, increasingly difficult AI opponents, and unique rewards. It uses a stamina system so you can progress steadily."
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="text-center mb-8">
        <h1 className="font-cinzel text-4xl md:text-5xl text-amber-300 uppercase tracking-wide mb-2">
          Frequently Asked Questions
        </h1>
        <p className="text-zinc-400 text-lg">Everything you need to know about Lanista Manager</p>
      </div>

      <div className="space-y-6">
        {faqs.map((faq, i) => {
          const Icon = faq.icon;
          return (
            <div key={i} className="bg-zinc-950 border border-amber-900/30 rounded p-6">
              <div className="flex items-start gap-3 mb-3">
                <Icon size={24} className="text-amber-500 shrink-0 mt-1" />
                <h2 className="font-cinzel text-xl text-amber-300">{faq.question}</h2>
              </div>
              <p className="text-zinc-300 leading-relaxed ml-9">{faq.answer}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-12 text-center border-t border-amber-900/30 pt-8">
        <p className="text-zinc-400 mb-6">Ready to begin your journey as a lanista?</p>
        <div className="flex gap-4 justify-center">
          <Link to="/login" className="bg-amber-600 hover:bg-amber-700 text-zinc-950 font-bold px-6 py-3 rounded transition-colors">
            Create Free Account
          </Link>
          <Link to="/about" className="border border-amber-600 text-amber-400 hover:bg-amber-950/30 font-semibold px-6 py-3 rounded transition-colors">
            Learn More
          </Link>
        </div>
      </div>

      <div className="mt-8 text-center">
        <p className="text-sm text-zinc-500">
          Still have questions? <Link to="/contact" className="text-amber-400 hover:underline">Contact us</Link>
        </p>
      </div>
    </div>
  );
}
